import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { openBugCount } from '../lib/bugReports';
import { isSubscribed, trialDaysLeft } from '../lib/billing';
import { enterStudentView } from '../lib/studentView';
import { useClassroom } from '../state/Classroom';

// The teacher shell: a sidebar with the class switcher and its destinations.
// No fish here, on purpose (the fish homescreen is for students), except the
// Ocean page, where the teacher awards them.
export default function TeacherLayout() {
  const { classrooms, active, setActiveId, students, session, billing, canCreate } = useClassroom();
  // The trial counts down in the last week; after it, every page says so.
  const days = trialDaysLeft(billing ?? null);
  const banner = !billing || billing.comped || isSubscribed(billing) ? null
    : !canCreate ? 'ended' : days <= 7 ? 'ending' : null;
  const navigate = useNavigate();
  const link = ({ isActive }: { isActive: boolean }) => (isActive ? 'active' : '');
  // Open bug reports for this class, refreshed on every page change.
  const where = useLocation();
  const [bugs, setBugs] = useState(0);
  useEffect(() => { if (active) openBugCount(active.id).then(setBugs); }, [active, where.pathname]);

  return (
    <div className="t-shell">
      <aside className="t-side">
        <NavLink to="/" className="brand">🐠 Gibberfish <span className="brand-sub">Classroom</span></NavLink>

        {classrooms.length > 0 && (
          <div className="class-switch">
            <label className="small muted" htmlFor="class-select">Class</label>
            <select
              id="class-select"
              value={active?.id ?? ''}
              onChange={e => {
                if (e.target.value === '__new') { navigate('/roster#new-class'); return; }
                setActiveId(e.target.value);
              }}
            >
              {classrooms.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              <option value="__new">+ New class…</option>
            </select>
          </div>
        )}

        <nav className="t-nav">
          <NavLink to="/" end className={link}><span aria-hidden>▦</span> Dashboard</NavLink>
          <NavLink to="/assignments" className={link}><span aria-hidden>☰</span> Assignments</NavLink>
          <NavLink to="/library" className={link}><span aria-hidden>📚</span> Library</NavLink>
          <NavLink to="/progress" className={link}><span aria-hidden>✓</span> Progress</NavLink>
          <NavLink to="/roster" className={link}>
            <span aria-hidden>☺</span> Students
            {students.length > 0 && <span className="count zero">{students.length}</span>}
          </NavLink>
          <NavLink to="/ocean" className={link}><span aria-hidden>🌊</span> Ocean</NavLink>
          <NavLink to="/game" className={link}><span aria-hidden>🎮</span> Class game</NavLink>
          <NavLink to="/shop" className={link}><span aria-hidden>🛍️</span> Shop</NavLink>
          <NavLink to="/bugs" className={link}>
            <span aria-hidden>🐞</span> Bug reports
            {bugs > 0 && <span className="count">{bugs}</span>}
          </NavLink>
        </nav>

        <NavLink to="/assign" className="new-btn">+ New assignment</NavLink>
        {active && <StudentViewButton classroomId={active.id} />}

        <div className="t-foot">
          <NavLink to="/billing" className={link}>
            {billing && !billing.comped && !isSubscribed(billing) ? `Free trial · ${days} day${days === 1 ? '' : 's'} left` : 'Plan & billing'}
          </NavLink>
          <NavLink to="/account" className={link}>{session?.user.email}</NavLink>
        </div>
      </aside>

      <main className="t-main">
        {banner && (
          <div className={`notice small ${banner === 'ended' ? 'bad' : ''}`} style={{ marginBottom: 18 }}>
            {banner === 'ended'
              ? <>Your free trial has ended. Students can still do what’s assigned; <Link to="/billing">subscribe</Link> to make new assignments.</>
              : <>{days} day{days === 1 ? '' : 's'} left in your free trial. <Link to="/billing">Choose a plan</Link> to keep going without a gap.</>}
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}

/** "👀 Student view": try the site as a student does, as this class's test student (lib/studentView.ts). */
function StudentViewButton({ classroomId }: { classroomId: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  return (
    <div className="student-view-btn">
      <button type="button" disabled={busy} title="See and try everything as a student does, as this class's test student. Their work isn't in your results."
        onClick={async () => { setBusy(true); setError(''); try { await enterStudentView(classroomId); } catch (e) { setError(e instanceof Error ? e.message : 'Couldn’t open student view.'); setBusy(false); } }}>
        {busy ? 'Opening…' : '👀 Student view'}
      </button>
      {error && <span className="small bad">{error}</span>}
    </div>
  );
}
